import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Panel, Stat } from "../components/common";
import { scoreMeta, fmt, pct, chgColor, regimeColor } from "../lib/format";
import { Globe, Activity, TrendingUp, DollarSign, ArrowRight } from "lucide-react";

const EU_INDEX = ["FEZ", "EWG", "EWQ", "EWI", "EWP", "EWU"];
const FX = ["EURUSD", "GBPUSD", "USDJPY", "DXY"];

function HeatTile({ a, onClick }) {
  const m = scoreMeta(a.smart_quant_score);
  return (
    <button data-testid={`europe-heat-${a.symbol}`} onClick={onClick}
      className="rounded-lg p-3 text-left hover:scale-[1.02] transition-transform"
      style={{ background: `${m.color}1A`, border: `1px solid ${m.color}44` }}>
      <div className="flex items-center justify-between">
        <span className="font-mono font-bold text-sm">{a.symbol}</span>
        <span className="font-mono text-xs font-bold tnum" style={{ color: m.color }}>{fmt(a.smart_quant_score, 0)}</span>
      </div>
      <div className="text-[10px] text-slate-400 truncate mt-0.5">{a.name}</div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] font-mono" style={{ color: m.color }}>{a.classification}</span>
        <span className="font-mono text-[11px] tnum" style={{ color: chgColor(a.change_1d) }}>{pct(a.change_1d)}</span>
      </div>
    </button>
  );
}

function FactorBar({ label, f }) {
  const col = f.score >= 55 ? "#10B981" : f.score >= 45 ? "#F59E0B" : "#EF4444";
  return (
    <div>
      <div className="flex justify-between text-[11px] font-mono mb-1">
        <span className="text-slate-400">{label}</span>
        <span style={{ color: col }}>{fmt(f.score, 0)} · {f.direction}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#141C2E] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${f.score}%`, background: col }} />
      </div>
    </div>
  );
}

export default function Europe() {
  const [assets, setAssets] = useState([]);
  const [gm, setGm] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/global-macro").then((r) => setGm(r.data)).catch(() => {});
  }, []);

  const bySym = Object.fromEntries(assets.map((a) => [a.symbol, a]));
  const indices = EU_INDEX.map((s) => bySym[s]).filter(Boolean);
  const forex = FX.map((s) => bySym[s]).filter(Boolean);
  const ranked = [...indices].sort((a, b) => b.smart_quant_score - a.smart_quant_score);
  const eu = gm?.eu_regime;
  const rc = eu ? regimeColor(eu.regime) : "#8B5CF6";

  return (
    <div className="space-y-5" data-testid="europe-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">european markets desk</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Globe size={26} className="text-[#22D3EE]" /> Europe Overview
          </h1>
        </div>
        <button data-testid="europe-macro-link" onClick={() => nav("/global-macro")}
          className="flex items-center gap-1.5 text-xs font-mono px-3 py-2 rounded-md border border-[#334155] text-slate-300 hover:bg-[#22D3EE12] hover:border-[#22D3EE44] hover:text-[#22D3EE] transition-colors">
          Macro Dashboard <ArrowRight size={13} />
        </button>
      </div>

      {/* --- Eurozone regime --- */}
      {eu && (
        <div className="panel p-4" data-testid="europe-regime-card" style={{ borderColor: `${rc}44` }}>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 md:w-80">
              <Activity size={22} style={{ color: rc }} />
              <div>
                <div className="eyebrow">eurozone regime</div>
                <div className="font-display text-xl font-extrabold" style={{ color: rc }}>{eu.label}</div>
                <div className="text-[11px] font-mono text-slate-500">confidence {eu.confidence}%</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
              {eu.factors && Object.entries(eu.factors).map(([k, f]) => (
                <FactorBar key={k} label={k} f={f} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- European indices heatmap --- */}
      <Panel title="European Indices — Smart Quant Score" icon={<TrendingUp size={13} />}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="europe-indices-heatmap">
          {indices.map((a) => <HeatTile key={a.symbol} a={a} onClick={() => nav(`/asset/${a.symbol}`)} />)}
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* --- forex --- */}
        <Panel title="Forex (majors + DXY)" icon={<DollarSign size={13} />}>
          <div className="space-y-1.5" data-testid="europe-forex-list">
            {forex.map((a) => {
              const m = scoreMeta(a.smart_quant_score);
              return (
                <button key={a.symbol} data-testid={`europe-fx-${a.symbol}`} onClick={() => nav(`/asset/${a.symbol}`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#141C2E] transition-colors text-left">
                  <span className="font-mono font-bold text-sm w-20">{a.symbol}</span>
                  <span className="text-xs text-slate-400 flex-1 truncate">{a.name}</span>
                  <span className="font-mono text-xs tnum w-16 text-right" style={{ color: chgColor(a.change_1d) }}>{pct(a.change_1d)}</span>
                  <span className="font-mono text-xs font-bold tnum w-10 text-right" style={{ color: m.color }}>{fmt(a.smart_quant_score, 0)}</span>
                </button>
              );
            })}
          </div>
        </Panel>

        {/* --- ranking --- */}
        <Panel title="European Index Ranking" icon={<TrendingUp size={13} />}>
          <div className="space-y-1.5" data-testid="europe-ranking">
            {ranked.map((a, i) => {
              const m = scoreMeta(a.smart_quant_score);
              return (
                <button key={a.symbol} onClick={() => nav(`/asset/${a.symbol}`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#141C2E] transition-colors text-left">
                  <span className="font-mono text-xs text-slate-500 w-5">#{i + 1}</span>
                  <span className="font-mono font-bold text-sm w-14">{a.symbol}</span>
                  <span className="text-xs text-slate-400 flex-1 truncate">{a.name}</span>
                  <span className="font-mono text-xs font-bold tnum px-1.5 py-0.5 rounded" style={{ color: m.color, background: `${m.color}18` }}>{fmt(a.smart_quant_score, 0)}</span>
                </button>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}
